import { create } from 'zustand';
import { useAuthStore } from './auth';

// Helper to get auth headers
const getAuthHeaders = (): HeadersInit => {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Todo item types
export interface InboxTodoItem {
  id: string;
  type: 'question' | 'post_review' | 'connector_action';
  title: string;
  description: string;
  employeeId: string;
  actionUrl: string;
  priority: 'high' | 'medium' | 'low';
  createdAt: string;
}

// Idea item types
export interface InboxIdea {
  id: number;
  employeeId: string;
  title: string;
  description: string;
  category: string;
  priority: 'high' | 'medium' | 'low';
  status: 'active' | 'dismissed' | 'accepted' | 'expired';
  expiresAt: string;
  actionUrl: string | null;
  createdAt: string;
}

interface InboxSidebarState {
  // Data
  todos: InboxTodoItem[];
  ideas: InboxIdea[];

  // UI State
  isOpen: boolean;
  isLoading: boolean;
  isGeneratingIdeas: boolean;
  activeTab: 'todos' | 'ideas' | 'emails' | 'slack';
  error: string | null;
  hasSeen: boolean; // Track if user has seen inbox in this session

  // Computed
  totalCount: number;
  badgeCount: number; // Count to show in badge (0 after seen)

  // Actions
  togglePanel: () => void;
  openPanel: () => void;
  closePanel: () => void;
  setActiveTab: (tab: 'todos' | 'ideas' | 'emails' | 'slack') => void;

  // Data fetching
  fetchData: () => Promise<void>;
  generateIdeas: () => Promise<void>;
  generateIdeasSilently: () => Promise<void>;
  dismissIdea: (ideaId: number) => Promise<void>;
  acceptIdea: (ideaId: number) => Promise<{ actionUrl: string | null }>;
  markAsSeen: () => Promise<void>;

  // Internal setters
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useInboxSidebarStore = create<InboxSidebarState>((set, get) => ({
  // Initial state
  todos: [],
  ideas: [],
  isOpen: false,
  isLoading: false,
  isGeneratingIdeas: false,
  activeTab: 'todos',
  error: null,
  totalCount: 0,
  badgeCount: 0,
  hasSeen: false,

  // UI Actions
  togglePanel: () => {
    const { isOpen, fetchData, markAsSeen } = get();
    if (!isOpen) {
      fetchData();
      markAsSeen(); // Clear badge when opening
    }
    set({ isOpen: !isOpen });
  },

  openPanel: () => {
    const { fetchData, markAsSeen } = get();
    fetchData();
    markAsSeen(); // Clear badge when opening
    set({ isOpen: true });
  },

  closePanel: () => {
    set({ isOpen: false });
  },

  setActiveTab: (tab) => {
    set({ activeTab: tab });
  },

  // Data fetching
  fetchData: async () => {
    const { isLoading, isGeneratingIdeas } = get();
    if (isLoading) return;

    set({ isLoading: true, error: null });

    try {
      const response = await fetch('/api/inbox/sidebar', {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch inbox data');
      }

      const data = await response.json() as { todos?: InboxTodoItem[]; ideas?: InboxIdea[] };

      // Filter out expired ideas - user should not see them
      const now = new Date();
      const activeIdeas = (data.ideas || []).filter((idea) => {
        const expiresAt = new Date(idea.expiresAt);
        return expiresAt.getTime() > now.getTime() && idea.status === 'active';
      });

      const newTotalCount = (data.todos?.length || 0) + activeIdeas.length;
      const { hasSeen } = get();

      set({
        todos: data.todos || [],
        ideas: activeIdeas,
        totalCount: newTotalCount,
        badgeCount: hasSeen ? 0 : newTotalCount, // Only show badge if not seen
        isLoading: false,
      });

      // Silently trigger idea generation in the background if no active ideas
      if (activeIdeas.length === 0 && !isGeneratingIdeas) {
        // Generate ideas silently without showing loading state to user
        get().generateIdeasSilently();
      }
    } catch (error) {
      console.error('Error fetching inbox data:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch inbox data',
        isLoading: false,
      });
    }
  },

  generateIdeas: async () => {
    set({ isGeneratingIdeas: true, error: null });

    try {
      const response = await fetch('/api/inbox/ideas/generate', {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to generate ideas');
      }

      // Refresh data after generation
      await get().fetchData();

      set({ isGeneratingIdeas: false });
    } catch (error) {
      console.error('Error generating ideas:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to generate ideas',
        isGeneratingIdeas: false,
      });
    }
  },

  // Silent idea generation - runs in background without showing loading state
  generateIdeasSilently: async () => {
    const { isGeneratingIdeas } = get();
    if (isGeneratingIdeas) return;

    set({ isGeneratingIdeas: true });

    try {
      const response = await fetch('/api/inbox/ideas/generate', {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        // Fetch new data silently after generation
        const dataResponse = await fetch('/api/inbox/sidebar', {
          headers: getAuthHeaders(),
        });

        if (dataResponse.ok) {
          const data = await dataResponse.json() as { todos?: InboxTodoItem[]; ideas?: InboxIdea[] };

          // Filter out expired ideas
          const now = new Date();
          const activeIdeas = (data.ideas || []).filter((idea) => {
            const expiresAt = new Date(idea.expiresAt);
            return expiresAt.getTime() > now.getTime() && idea.status === 'active';
          });

          set({
            todos: data.todos || [],
            ideas: activeIdeas,
            totalCount: (data.todos?.length || 0) + activeIdeas.length,
          });
        }
      }
    } catch (error) {
      // Silent failure - don't show error to user
      console.error('Silent idea generation failed:', error);
    } finally {
      set({ isGeneratingIdeas: false });
    }
  },

  dismissIdea: async (ideaId) => {
    try {
      const response = await fetch(`/api/inbox/ideas/${ideaId}/dismiss`, {
        method: 'PUT',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to dismiss idea');
      }

      // Update local state
      set((state) => ({
        ideas: state.ideas.filter((idea) => idea.id !== ideaId),
        totalCount: state.totalCount - 1,
      }));
    } catch (error) {
      console.error('Error dismissing idea:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to dismiss idea',
      });
    }
  },

  acceptIdea: async (ideaId) => {
    try {
      const response = await fetch(`/api/inbox/ideas/${ideaId}/accept`, {
        method: 'PUT',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to accept idea');
      }

      const data = await response.json() as {
        success: boolean;
        actionUrl?: string | null;
        employeeId?: string;
      };

      // Update local state
      set((state) => ({
        ideas: state.ideas.filter((idea) => idea.id !== ideaId),
        totalCount: state.totalCount - 1,
      }));

      // Return the action URL (or construct one from employeeId)
      const targetUrl = data.actionUrl || (data.employeeId ? `/dashboard/employees/${data.employeeId}` : null);
      return { actionUrl: targetUrl };
    } catch (error) {
      console.error('Error accepting idea:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to accept idea',
      });
      return { actionUrl: null };
    }
  },

  // Mark inbox as seen (clears badge)
  markAsSeen: async () => {
    const { hasSeen } = get();
    if (hasSeen) return; // Already seen

    set({ hasSeen: true, badgeCount: 0 });

    try {
      await fetch('/api/inbox/mark-seen', {
        method: 'POST',
        headers: getAuthHeaders(),
      });
    } catch (error) {
      // Silent failure - badge is already cleared locally
      console.error('Failed to mark inbox as seen:', error);
    }
  },

  // Internal setters
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
}));

// Helper to get employee display info
export const EMPLOYEE_INFO: Record<string, { name: string; color: string; avatar: string }> = {
  aria: { name: 'Aria', color: '#EC4899', avatar: '/employees/aria.png' },
  nova: { name: 'Nova', color: '#8B5CF6', avatar: '/employees/nova.png' },
  sage: { name: 'Sage', color: '#10B981', avatar: '/employees/sage.png' },
  echo: { name: 'Echo', color: '#3B82F6', avatar: '/employees/echo.png' },
  atlas: { name: 'Atlas', color: '#F59E0B', avatar: '/employees/atlas.png' },
  spark: { name: 'Spark', color: '#EF4444', avatar: '/employees/spark.png' },
  orbit: { name: 'Orbit', color: '#06B6D4', avatar: '/employees/orbit.png' },
  comet: { name: 'Comet', color: '#8B5CF6', avatar: '/employees/comet.png' },
  flux: { name: 'Flux', color: '#F97316', avatar: '/employees/flux.png' },
  ink: { name: 'Ink', color: '#6366F1', avatar: '/employees/ink.png' },
  code: { name: 'Code', color: '#14B8A6', avatar: '/employees/code.png' },
  brain: { name: 'Brain AI', color: '#8B5CF6', avatar: '/employees/brain.png' },
};

// Helper to check if an idea is expired
export function isIdeaExpired(expiresAt: string): boolean {
  const now = new Date();
  const expires = new Date(expiresAt);
  return expires.getTime() <= now.getTime();
}

// Helper to format time remaining
export function formatTimeRemaining(expiresAt: string): string {
  const now = new Date();
  const expires = new Date(expiresAt);
  const diff = expires.getTime() - now.getTime();

  if (diff <= 0) return 'Expiring soon';

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m left`;
  }
  return `${minutes}m left`;
}

// Helper to get priority color
export function getPriorityColor(priority: 'high' | 'medium' | 'low'): string {
  switch (priority) {
    case 'high':
      return '#EF4444';
    case 'medium':
      return '#F59E0B';
    case 'low':
      return '#10B981';
    default:
      return '#6B7280';
  }
}
